import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { Program } from "@/lib/mock-data";

interface ApiProgramsResponse {
  published: any[];
  drafts: any[];
  userPrograms?: any[];
}

function mapProgramData(p: any): Program & { recommendedPlaylistId?: string } {
  return {
    id: p.id,
    title: p.title,
    description: p.description || '',
    difficulty: p.difficulty || 'Intermediate',
    duration: p.duration || '45 min',
    totalWeeks: p.totalWeeks || 8,
    image: p.imageUrl || p.image || '',
    sections: p.sections || [],
    sessions: p.sessions,
    schedule: p.schedule,
    spotifyPlaylistUrl: p.spotifyPlaylistUrl,
    badgeStatus: p.badgeStatus,
    experienceLevel: p.experienceLevel,
    muscleGroups: p.muscleGroups,
    createdBy: p.createdBy,
    status: p.status,
    recommendedPlaylistId: p.recommendedPlaylistId,
  };
}

export function useApiPrograms() {
  const { data: apiProgramsData, isLoading } = useQuery<ApiProgramsResponse>({
    queryKey: ['/api/programs'],
    staleTime: 5 * 60 * 1000,
  });

  const apiPrograms: Program[] = useMemo(() => {
    if (!apiProgramsData?.published) return [];
    return apiProgramsData.published.map(mapProgramData);
  }, [apiProgramsData]);

  const draftPrograms: Program[] = useMemo(() => {
    if (!apiProgramsData?.drafts) return [];
    return apiProgramsData.drafts.map(mapProgramData);
  }, [apiProgramsData]);

  const userPrograms: Program[] = useMemo(() => {
    if (!apiProgramsData?.userPrograms) return [];
    return apiProgramsData.userPrograms.map(mapProgramData);
  }, [apiProgramsData]);

  const allPrograms: Program[] = useMemo(() => {
    return [...apiPrograms, ...draftPrograms, ...userPrograms];
  }, [apiPrograms, draftPrograms, userPrograms]);

  return { apiPrograms, draftPrograms, userPrograms, allPrograms, isLoading, apiProgramsData };
}
